import { Link, Paper, Stack, Typography } from '@mui/material';

const sectionTitleSx = { fontWeight: 700 } as const;

export default function ExtensionPrivacyPolicy() {
    return (
        <Stack alignItems="center" px={2} py={{ xs: 4, md: 6 }}>
            <Paper elevation={2} sx={{ maxWidth: 900, p: { xs: 3, md: 5 }, width: '100%' }}>
                <Stack spacing={3}>
                    <Stack spacing={1}>
                        <Typography component="h1" variant="h4">
                            Privacy Policy for Lexo Connect
                        </Typography>
                        <Typography color="text.secondary" variant="body1">
                            Effective Date: Feb 27, 2026
                        </Typography>
                    </Stack>

                    <Typography variant="body1">
                        Lexo Connect (&ldquo;The Extension&rdquo;) helps legal teams transfer
                        prospect and matter information from Lexo into conveyancing software in a
                        secure and efficient way.
                    </Typography>

                    <Stack spacing={1.5}>
                        <Typography variant="h6" sx={sectionTitleSx}>
                            1. Data Accessed
                        </Typography>
                        <Typography variant="body1">
                            To support autofill workflows, the Extension accesses:
                        </Typography>
                        <Stack component="ul" spacing={0.75} sx={{ m: 0, pl: 3 }}>
                            <Typography component="li" variant="body1">
                                Lexo data required for autofill, such as contact details, matter
                                details, property details, and supporting document files
                            </Typography>
                            <Typography component="li" variant="body1">
                                Field values and selected document metadata on supported
                                conveyancing platforms (for example, existing field values and
                                uploaded file names) to determine whether fields should be filled
                                and to report results
                            </Typography>
                            <Typography component="li" variant="body1">
                                Authentication response data from Azure AD B2C needed to sign users
                                in to Lexo APIs
                            </Typography>
                        </Stack>
                    </Stack>

                    <Stack spacing={1.5}>
                        <Typography variant="h6" sx={sectionTitleSx}>
                            2. Data Handling
                        </Typography>
                        <Stack component="ul" spacing={0.75} sx={{ m: 0, pl: 3 }}>
                            <Typography component="li" variant="body1">
                                The Extension stores authentication tokens in browser extension
                                local storage to maintain sign-in state between sessions.
                            </Typography>
                            <Typography component="li" variant="body1">
                                Autofill data is submitted to supported conveyancing platforms
                                through the user&apos;s browser session.
                            </Typography>
                            <Typography component="li" variant="body1">
                                The Extension sends workflow/report data to Lexo APIs, including
                                field-level autofill outcomes (for example: filled, correct,
                                mismatch, ignored, or error) so users can review completion results.
                            </Typography>
                            <Typography component="li" variant="body1">
                                Data transmission to Lexo, Azure AD B2C, and supported conveyancing
                                platforms occurs over HTTPS.
                            </Typography>
                        </Stack>
                    </Stack>

                    <Stack spacing={1.5}>
                        <Typography variant="h6" sx={sectionTitleSx}>
                            3. Authentication
                        </Typography>
                        <Stack component="ul" spacing={0.75} sx={{ m: 0, pl: 3 }}>
                            <Typography component="li" variant="body1">
                                Users authenticate through Azure AD B2C using the browser extension
                                identity flow.
                            </Typography>
                            <Typography component="li" variant="body1">
                                The Extension does not store user passwords.
                            </Typography>
                            <Typography component="li" variant="body1">
                                Access and refresh tokens are stored in browser extension local
                                storage and can be cleared by logging out or removing the Extension.
                            </Typography>
                        </Stack>
                    </Stack>

                    <Stack spacing={1.5}>
                        <Typography variant="h6" sx={sectionTitleSx}>
                            4. Analytics and Sharing
                        </Typography>
                        <Stack component="ul" spacing={0.75} sx={{ m: 0, pl: 3 }}>
                            <Typography component="li" variant="body1">
                                The Extension does not use third-party advertising or cross-site
                                tracking analytics.
                            </Typography>
                            <Typography component="li" variant="body1">
                                Data processed by the Extension is used only to authenticate users,
                                retrieve Lexo records, autofill supported conveyancing platform
                                forms, upload associated documents, and generate autofill reports
                                for users.
                            </Typography>
                            <Typography component="li" variant="body1">
                                Data is not sold.
                            </Typography>
                        </Stack>
                    </Stack>

                    <Stack spacing={1.5}>
                        <Typography variant="h6" sx={sectionTitleSx}>
                            5. User Control
                        </Typography>
                        <Stack component="ul" spacing={0.75} sx={{ m: 0, pl: 3 }}>
                            <Typography component="li" variant="body1">
                                Users can log out of the Extension, which clears locally stored
                                authentication tokens.
                            </Typography>
                            <Typography component="li" variant="body1">
                                The Extension can be disabled or removed through standard browser
                                controls at any time.
                            </Typography>
                        </Stack>
                    </Stack>

                    <Stack spacing={1.5}>
                        <Typography variant="h6" sx={sectionTitleSx}>
                            6. Permissions
                        </Typography>
                        <Typography variant="body1">
                            The Extension requests the following Chrome permissions:
                        </Typography>
                        <Stack component="ul" spacing={0.75} sx={{ m: 0, pl: 3 }}>
                            <Typography component="li" variant="body1">
                                <strong>identity</strong> - to complete Azure AD B2C sign-in flow
                            </Typography>
                            <Typography component="li" variant="body1">
                                <strong>storage</strong> - to store authentication tokens for
                                session continuity
                            </Typography>
                            <Typography component="li" variant="body1">
                                Host access to Lexo domains, Azure AD B2C login domains, and
                                supported conveyancing platform pages used by the autofill workflow
                            </Typography>
                        </Stack>
                        <Typography variant="body1">
                            The Extension does not request unrelated site access or broad browsing
                            history access.
                        </Typography>
                    </Stack>

                    <Stack spacing={1.5}>
                        <Typography variant="h6" sx={sectionTitleSx}>
                            7. Contact
                        </Typography>
                        <Typography variant="body1">
                            For questions regarding privacy or data handling, please contact us at:{' '}
                            <Link href="mailto:support@lexo.ai">support@lexo.ai</Link>
                        </Typography>
                    </Stack>
                </Stack>
            </Paper>
        </Stack>
    );
}
